import * as signalR from '@microsoft/signalr';
import { NEXT_PUBLIC_API_BASE_URL } from '@env';
import { Logger } from '../utils/logger';
import { getAccessToken } from './tokenManager';

/**
 * The parent app's live connection to the notification hub.
 *
 * FCM and SignalR do different jobs and the app needs both. FCM wakes a phone
 * that is in the background or shut; SignalR is what makes a notification appear
 * while a parent is actually looking at the screen. Without it the app stayed
 * silent until it was closed and reopened.
 *
 * The server puts every authenticated connection into a group named for the
 * caller's own user id, so nothing here has to subscribe to anything - being
 * connected is enough to receive that parent's messages, and only theirs.
 */

export interface SignalRMessage {
  messageId: string;
  eventType: string;
  eventDescription: string;
  payload: any;
  correlationId?: string;
  createdAt: string;
}

type MessageHandler = (msg: SignalRMessage) => void;

const HUB_PATH = '/hubs/notifications';

// One connection per app, held at module scope: a second socket would deliver
// every notification twice.
let connection: signalR.HubConnection | null = null;
let starting: Promise<signalR.HubConnection | null> | null = null;

const handlers = new Set<MessageHandler>();

/** Registers a listener. Returns the function that removes it again. */
export const onSignalRMessage = (handler: MessageHandler): (() => void) => {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
};

const dispatch = (msg: SignalRMessage) => {
  handlers.forEach((handler) => {
    try {
      handler(msg);
    } catch (error) {
      // One bad listener must not swallow the message for the others.
      Logger.error('SignalR', 'A message handler threw', error);
    }
  });
};

export const isSignalRConnected = (): boolean =>
  connection?.state === signalR.HubConnectionState.Connected;

/**
 * Opens the connection, or returns the one already open.
 *
 * Concurrent callers share a single in-flight attempt. Mount effects and an
 * auth-state change can both ask at once, and two builders racing would leave a
 * duplicate socket behind.
 */
export const startSignalR = async (): Promise<signalR.HubConnection | null> => {
  if (connection?.state === signalR.HubConnectionState.Connected) {
    return connection;
  }

  if (starting) return starting;

  starting = (async () => {
    try {
      if (!NEXT_PUBLIC_API_BASE_URL) {
        Logger.warn('SignalR', 'No API base URL configured - not connecting.');
        return null;
      }

      const conn = new signalR.HubConnectionBuilder()
        .withUrl(`${NEXT_PUBLIC_API_BASE_URL}${HUB_PATH}`, {
          // Called again on every reconnect, so an expired token is replaced
          // rather than retried. tokenManager caches, so this is cheap.
          accessTokenFactory: async () => (await getAccessToken()) ?? '',
        })
        // Backs off rather than hammering: a phone loses its connection every
        // time it changes network or sleeps, which is constantly.
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      conn.on('ReceiveMessage', (msg: SignalRMessage) => {
        Logger.info('SignalR', `Received ${msg?.eventType ?? 'unknown'}`);
        dispatch(msg);
      });

      conn.onreconnecting((error) => {
        Logger.warn('SignalR', 'Reconnecting', error);
      });

      conn.onreconnected(() => {
        Logger.info('SignalR', 'Reconnected');
      });

      conn.onclose((error) => {
        Logger.warn('SignalR', 'Connection closed', error);
        // Cleared so the next start builds a fresh connection. Reusing a closed
        // one throws, and automatic reconnect has already given up by this point.
        connection = null;
        starting = null;
      });

      await conn.start();
      connection = conn;
      Logger.info('SignalR', 'Connected');
      return conn;
    } catch (error) {
      // A failure here must never break the app: FCM still delivers, so the
      // parent loses live updates, not notifications altogether.
      Logger.error('SignalR', 'Could not connect', error);
      connection = null;
      return null;
    } finally {
      starting = null;
    }
  })();

  return starting;
};

/** Closes the connection, e.g. on sign-out. Safe to call when not connected. */
export const stopSignalR = async (): Promise<void> => {
  const conn = connection;
  connection = null;
  starting = null;

  if (!conn) return;

  try {
    await conn.stop();
    Logger.info('SignalR', 'Disconnected');
  } catch (error) {
    Logger.warn('SignalR', 'Error while disconnecting', error);
  }
};
